import { Container, useMediaQuery } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import csv from "jquery-csv";
import React from "react";
import Map from "./Map";
import Table, {
  FILTER_SANE_ADULT_ACUTE,
  FILTER_SANE_ADULT_NON_ACUTE,
  FILTER_SANE_ADOLESCENT_ACUTE,
  FILTER_SANE_ADOLESCENT_NON_ACUTE,
  FILTER_SANE_PEDIATRIC_ACUTE,
  FILTER_SANE_PEDIATRIC_NON_ACUTE,
  FILTER_SANE_CHILD_ABUSE,
  FILTER_SANE_IPV,
  FILTER_SANE_ELDER,
  FILTER_SANE_TRAUMA,
  FILTER_SANE_SUSPECT,
} from "./Table";
import {
  AFTER_HOURS_TELEPHONE,
  CountyFacilities,
  Facility,
  MarkerCoordinates,
  NetworkFacility,
  PROGRAM_SANE,
  COUNTIES_SERVICED,
  PROGRAM_ADOLESCENT_ACUTE,
  PROGRAM_ADOLESCENT_NON_ACUTE,
  PROGRAM_ADULT_ACUTE,
  PROGRAM_ADULT_NON_ACUTE,
  PROGRAM_PEDIATRIC_ACUTE,
  PROGRAM_PEDIATRIC_NON_ACUTE,
  FACILITY_TYPE_CAC,
  FACILITY_TYPE_RCC,
  FACILITY_TYPE_TT,
  PROGRAM_CHILD_ABUSE,
  PROGRAM_ELDER_MALTREATMENT,
  PROGRAM_IPV,
  PROGRAM_TRAUMA,
  PROGRAM_SUSPECT,
  SERVICE_HOURS,
  SANE_24_7_AVAILABILITY,
  AGES_SERVED,
} from "./types";
import { createTheme } from "@mui/material/styles";
import { capitalizeWord } from "./utils";
const DATA_URL: string =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vS2m4NPtXtsEhLnrGZa7uNNDeSGTfSs0vLe1TOWGPYdJ7vOCRW1LHReB-sqmKbrvZ-qaqnjIyAcCE5G/pub?output=csv";

const theme = createTheme({
  typography: {
    allVariants: {
      textAlign: "left",
    },
    fontFamily:
      '"Open Sans","Helvetica Neue","Helvetica",Helvetica,Arial,sans-serif',
    h1: {
      fontSize: "1.3rem",
    },
    h2: {
      fontSize: "1.25rem",
    },
    h3: {
      fontSize: "1.2rem",
    },
  },
});

function App() {
  const isMobile = useMediaQuery("(max-width: 400px)");

  const saneProgramsCoords = React.useRef<MarkerCoordinates[]>([]);
  const cacFacilityCoords = React.useRef<MarkerCoordinates[]>([]);
  const rccFacilityCoords = React.useRef<MarkerCoordinates[]>([]);
  const ttFacilityCoords = React.useRef<MarkerCoordinates[]>([]);
  const numRccFacilities = React.useRef<{
    [countyName: string]: [number, number];
  }>({});
  const numCacFacilities = React.useRef<{
    [countyName: string]: [number, number];
  }>({});
  const numSanePrograms = React.useRef<{
    [countyName: string]: [number, number];
  }>({});
  const numTtFacilities = React.useRef<{
    [countyName: string]: [number, number];
  }>({});
  const [facilityData, setFacilityData] = React.useState<CountyFacilities>({});

  const [selectedCounty, setSelectedCounty] = React.useState<string>("");

  const onSelectedCountyChange = (p: string) => {
    setSelectedCounty(p);
  };
  async function fetchFacilityData(): Promise<CountyFacilities> {
    const fetchedData = await fetch(DATA_URL, {
      redirect: "follow",
    }).then((r) => r.text()).catch(e => {
      console.error("Failed to read facility data from google sheet")
    });
    const parsedFacilities = csv.toObjects(fetchedData);

    const facilities = parsedFacilities.reduce(
      (agg: CountyFacilities, curr: NetworkFacility) => {
        const coordinates: MarkerCoordinates = [
          parseFloat(curr.LONGITUDE),
          parseFloat(curr.LATITUDE),
        ];
        const parsedFacility = parseNetworkFacility(curr);
        const countyName = parsedFacility.county;
        if (parsedFacility.saneProgram) {
          if (!numSanePrograms.current[countyName]) {
            numSanePrograms.current[countyName] = [0, 0];
          }
          numSanePrograms.current[countyName][0] =
            numSanePrograms.current[countyName][0] + 1;

          if(validCoordinates(coordinates)) {
            saneProgramsCoords.current.push(coordinates);
          }
        }

        if (parsedFacility.type.toLowerCase() === FACILITY_TYPE_CAC.toLowerCase()) {
          if (!numCacFacilities.current[countyName]) {
            numCacFacilities.current[countyName] = [0, 0];
          }
          numCacFacilities.current[countyName][0] =
            numCacFacilities.current[countyName][0] + 1;

          if(validCoordinates(coordinates)) {
            cacFacilityCoords.current.push(coordinates);
          }
        }

        if (parsedFacility.type.toLowerCase() === FACILITY_TYPE_RCC.toLowerCase()) {
          if (!numRccFacilities.current[countyName]) {
            numRccFacilities.current[countyName] = [0, 0];
          }
          numRccFacilities.current[countyName][0] =
            numRccFacilities.current[countyName][0] + 1;

          if(validCoordinates(coordinates)) {
            rccFacilityCoords.current.push(coordinates);
          }
        }

        if (parsedFacility.type.toLowerCase() === FACILITY_TYPE_TT.toLowerCase()) {
          if (!numTtFacilities.current[countyName]) {
            numTtFacilities.current[countyName] = [0, 0];
          }
          numTtFacilities.current[countyName][0] =
            numTtFacilities.current[countyName][0] + 1;

          if(validCoordinates(coordinates)) {
            ttFacilityCoords.current.push(coordinates);
          }
        }
        const countyFacilities = agg[countyName] ?? [];

        if (parsedFacility.servicedCounties) {
          const servicedCounties = parsedFacility.servicedCounties;
          servicedCounties.forEach((servedCountyName: string) => {
            if (servedCountyName === countyName) return;
            if (parsedFacility.saneProgram) {
              if (!numSanePrograms.current[servedCountyName]) {
                numSanePrograms.current[servedCountyName] = [0, 0];
              }
              numSanePrograms.current[servedCountyName][1] =
                numSanePrograms.current[servedCountyName][1] + 1;
            }

            if (parsedFacility.type.toLowerCase() === FACILITY_TYPE_CAC.toLowerCase()) {
              if (!numCacFacilities.current[servedCountyName]) {
                numCacFacilities.current[servedCountyName] = [0, 0];
              }
              numCacFacilities.current[servedCountyName][1] =
                numCacFacilities.current[servedCountyName][1] + 1;
            }

            if (parsedFacility.type.toLowerCase() === FACILITY_TYPE_RCC.toLowerCase()) {
              if (!numRccFacilities.current[servedCountyName]) {
                numRccFacilities.current[servedCountyName] = [0, 0];
              }
              numRccFacilities.current[servedCountyName][1] =
                numRccFacilities.current[servedCountyName][1] + 1;
            }

            if (parsedFacility.type.toLowerCase() === FACILITY_TYPE_TT.toLowerCase()) {
              if (!numTtFacilities.current[servedCountyName]) {
                numTtFacilities.current[servedCountyName] = [0, 0];
              }
              numTtFacilities.current[servedCountyName][1] =
                numTtFacilities.current[servedCountyName][1] + 1;
            }

            const servicedCountyFacilities = agg[servedCountyName] ?? [];

            agg[servedCountyName] = [
              ...servicedCountyFacilities,
              parsedFacility,
            ];
          });
        }
        agg[countyName] = [...countyFacilities, parsedFacility];
        return agg;
      },
      {}
    );

    return facilities;
  }

  React.useEffect(() => {
    fetchFacilityData().then((data) => setFacilityData(data));
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ justifyContent: "center" }}>
        <Map
          facilityData={facilityData}
          numSanePrograms={numSanePrograms.current}
          saneProgramsCoords={saneProgramsCoords.current}
          selectedCounty={selectedCounty}
          onSelectedCountyChange={onSelectedCountyChange}
          cacFacilityCoords={cacFacilityCoords.current}
          numCacFacilities={numCacFacilities.current}
          rccFacilityCoords={rccFacilityCoords.current}
          numRccFacilities={numRccFacilities.current}
          ttFacilityCoords={ttFacilityCoords.current}
          numTtFacilities={numTtFacilities.current}
          isMobile={isMobile}
        />
        <Table
          counties={facilityData}
          onSelectCounty={onSelectedCountyChange}
          selectedCounty={selectedCounty}
          isMobile={isMobile}
        />
      </Container>
    </ThemeProvider>
  );
}

export default App;

function parseNetworkFacility(networkFacility: NetworkFacility): Facility {
  var programs: { [program: string]: string } = {};
  if (networkFacility[PROGRAM_ADULT_ACUTE] === "TRUE") programs[FILTER_SANE_ADULT_ACUTE] = PROGRAM_ADULT_ACUTE;
  if (networkFacility[PROGRAM_ADULT_NON_ACUTE] === "TRUE") programs[FILTER_SANE_ADULT_NON_ACUTE] = PROGRAM_ADULT_NON_ACUTE;
  if (networkFacility[PROGRAM_ADOLESCENT_ACUTE] === "TRUE") programs[FILTER_SANE_ADOLESCENT_ACUTE] = PROGRAM_ADOLESCENT_ACUTE;
  if (networkFacility[PROGRAM_ADOLESCENT_NON_ACUTE] === "TRUE") programs[FILTER_SANE_ADOLESCENT_NON_ACUTE] = PROGRAM_ADOLESCENT_NON_ACUTE;
  if (networkFacility[PROGRAM_PEDIATRIC_ACUTE] === "TRUE") programs[FILTER_SANE_PEDIATRIC_ACUTE] = PROGRAM_PEDIATRIC_ACUTE;
  if (networkFacility[PROGRAM_PEDIATRIC_NON_ACUTE] === "TRUE") programs[FILTER_SANE_PEDIATRIC_NON_ACUTE] = PROGRAM_PEDIATRIC_NON_ACUTE;
  if (networkFacility[PROGRAM_CHILD_ABUSE] === "TRUE") programs[FILTER_SANE_CHILD_ABUSE] = PROGRAM_CHILD_ABUSE;
  if (networkFacility[PROGRAM_IPV] === "TRUE") programs[FILTER_SANE_IPV] = PROGRAM_IPV;
  if (networkFacility[PROGRAM_ELDER_MALTREATMENT] === "TRUE") programs[FILTER_SANE_ELDER] = PROGRAM_ELDER_MALTREATMENT;
  if (networkFacility[PROGRAM_TRAUMA] === "TRUE") programs[FILTER_SANE_TRAUMA] = PROGRAM_TRAUMA;
  if (networkFacility[PROGRAM_SUSPECT] === "TRUE") programs[FILTER_SANE_SUSPECT] = PROGRAM_SUSPECT;
  if (networkFacility.TYPE.toLowerCase() === FACILITY_TYPE_RCC.toLowerCase() || networkFacility.TYPE.toLowerCase() === FACILITY_TYPE_CAC.toLowerCase() || networkFacility.TYPE.toLowerCase() === FACILITY_TYPE_TT.toLowerCase())
    programs[networkFacility.TYPE] = networkFacility.TYPE;

  return {
    programs: programs,
    name: networkFacility.NAME,
    address: networkFacility.ADDRESS,
    city: networkFacility.CITY,
    state: networkFacility.STATE,
    zip: networkFacility.ZIP,
    county: networkFacility.COUNTY.split(" ").map(capitalizeWord).join(" "),
    servicedCounties: networkFacility[COUNTIES_SERVICED]?.split(";").map(s => s.trim()),
    tel: networkFacility.TELEPHONE,
    afterHoursTel: networkFacility[AFTER_HOURS_TELEPHONE],
    lat: networkFacility.LATITUDE,
    long: networkFacility.LONGITUDE,
    website: networkFacility.WEBSITE,
    email: networkFacility.EMAIL,
    saneProgram: networkFacility[PROGRAM_SANE] === "TRUE",
    pediatricAcute: networkFacility[PROGRAM_PEDIATRIC_ACUTE] === "TRUE",
    pediatricNonAcute: networkFacility[PROGRAM_PEDIATRIC_NON_ACUTE] === "TRUE",
    adolescentAcute: networkFacility[PROGRAM_ADOLESCENT_ACUTE] === "TRUE",
    adolescentNonAcute: networkFacility[PROGRAM_ADOLESCENT_NON_ACUTE] === "TRUE",
    adultAcute: networkFacility[PROGRAM_ADULT_ACUTE] === "TRUE",
    adultNonAcute: networkFacility[PROGRAM_ADULT_NON_ACUTE] === "TRUE",
    child: networkFacility[PROGRAM_CHILD_ABUSE] === "TRUE",
    ipv: networkFacility[PROGRAM_IPV] === "TRUE",
    elder: networkFacility[PROGRAM_ELDER_MALTREATMENT] === "TRUE",
    trauma: networkFacility[PROGRAM_TRAUMA] === "TRUE",
    suspect: networkFacility[PROGRAM_SUSPECT] === "TRUE",
    ages: networkFacility[AGES_SERVED],
    type: networkFacility.TYPE,
    serviceHours: networkFacility[SERVICE_HOURS],
    sane247Availability: networkFacility[SANE_24_7_AVAILABILITY] === "TRUE",
    note: networkFacility.NOTE,
  };
}

function validCoordinates(coordinates: MarkerCoordinates): boolean {
  if(isNaN(coordinates[0]) || isNaN(coordinates[1])) {
    return false;
  }
  return true;
}
